import { jsx as _jsx } from "preact/jsx-runtime";
// @ts-ignore
import { createRootFragment } from "preact-root-fragment";
import { render } from "preact";
import { useRef, useEffect } from "preact/hooks";
// @ts-ignore
import vertShader from "./shaders/gradient.vert";
// @ts-ignore
import fragShader from "./shaders/gradient.frag";
const VERT = vertShader;
const FRAG = fragShader;
const parseColor = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return [r, g, b];
};
const GradientGlow = ({ colors = ["#C51C1C", "#F05A14", "#FD1169", "#00A19A", "#AA28F5"], speed = 5, width = window.innerWidth, height = 5000, lightCount = 7, colorSpeed = 6, radiusBase = 0.25, radiusVariation = 0.25, radiusPulseSpeed = 0.05, radiusPulseAmount = 0.12, }) => {
    const canvasRef = useRef(null);
    const animRef = useRef(0);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const gl = canvas.getContext("webgl2", {
            alpha: true,
            premultipliedAlpha: true,
            antialias: false,
            powerPreference: "low-power",
        });
        if (!gl)
            return;
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        const compileShader = (src, type) => {
            const s = gl.createShader(type);
            gl.shaderSource(s, src);
            gl.compileShader(s);
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
                console.error(gl.getShaderInfoLog(s));
                return null;
            }
            return s;
        };
        const vs = compileShader(VERT, gl.VERTEX_SHADER);
        const fs = compileShader(FRAG, gl.FRAGMENT_SHADER);
        if (!vs || !fs)
            return;
        const prog = gl.createProgram();
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(prog));
            return;
        }
        gl.useProgram(prog);
        const verts = new Float32Array([-1, -1, 3, -1, -1, 3]);
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
        const aPos = gl.getAttribLocation(prog, "a_position");
        gl.enableVertexAttribArray(aPos);
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
        const uResolution = gl.getUniformLocation(prog, "u_resolution");
        const uTime = gl.getUniformLocation(prog, "u_time");
        const uSpeed = gl.getUniformLocation(prog, "u_speed");
        const uColorSpeed = gl.getUniformLocation(prog, "u_colorSpeed");
        const uLightCount = gl.getUniformLocation(prog, "u_lightCount");
        const uColorCount = gl.getUniformLocation(prog, "u_colorCount");
        const uRadiusBase = gl.getUniformLocation(prog, "u_radiusBase");
        const uRadiusVariation = gl.getUniformLocation(prog, "u_radiusVariation");
        const uRadiusPulseSpeed = gl.getUniformLocation(prog, "u_radiusPulseSpeed");
        const uRadiusPulseAmount = gl.getUniformLocation(prog, "u_radiusPulseAmount");
        const uColors = Array.from({ length: 8 }, (_, i) => gl.getUniformLocation(prog, `u_colors[${i}]`));
        const parsed = colors.map(parseColor);
        while (parsed.length < 8)
            parsed.push([0, 0, 0]);
        gl.uniform1f(uSpeed, speed);
        gl.uniform1f(uColorSpeed, colorSpeed);
        gl.uniform1i(uLightCount, lightCount);
        gl.uniform1i(uColorCount, colors.length);
        gl.uniform1f(uRadiusBase, radiusBase);
        gl.uniform1f(uRadiusVariation, radiusVariation);
        gl.uniform1f(uRadiusPulseSpeed, radiusPulseSpeed);
        gl.uniform1f(uRadiusPulseAmount, radiusPulseAmount);
        parsed.forEach((c, i) => gl.uniform3fv(uColors[i], c));
        let startTime = performance.now();
        const render = () => {
            const time = (performance.now() - startTime) / 1000;
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.uniform2f(uResolution, canvas.width, canvas.height);
            gl.uniform1f(uTime, time);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
            animRef.current = requestAnimationFrame(render);
        };
        render();
        return () => {
            cancelAnimationFrame(animRef.current);
            gl.deleteProgram(prog);
            gl.deleteBuffer(buf);
        };
    }, [
        colors,
        speed,
        width,
        height,
        lightCount,
        colorSpeed,
        radiusBase,
        radiusVariation,
        radiusPulseSpeed,
        radiusPulseAmount,
    ]);
    return (_jsx("canvas", { ref: canvasRef, width: width, height: height, style: {
            display: "block",
            width: "100%",
            height: `${height}px`,
        } }));
};
function addBlur(el, args = {}) {
    const wrapElement = (element) => {
        var _a;
        const container = document.createElement("div");
        container.classList.add("soul-gradient-container");
        (_a = element.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(container, element);
        container.appendChild(element);
        const target = document.createElement("div");
        container.appendChild(target);
        return [container, target];
    };
    const defaultProps = {
        colors: ["#C51C1C", "#F05A14", "#FD1169", "#00A19A", "#AA28F5"],
        speed: 5,
        width: window.innerWidth,
        height: 3000,
        lightCount: 7,
        colorSpeed: 6,
        radiusBase: 0.25,
        radiusVariation: 0.35,
        radiusPulseSpeed: 0.25,
        radiusPulseAmount: 0.12,
    };
    const props = Object.assign(Object.assign({}, defaultProps), args);
    const [container, target] = wrapElement(el);
    render(_jsx(GradientGlow, { colors: props.colors, speed: props.speed, width: props.width, height: props.height, lightCount: props.lightCount, colorSpeed: props.colorSpeed, radiusBase: props.radiusBase, radiusVariation: props.radiusVariation, radiusPulseSpeed: props.radiusPulseSpeed, radiusPulseAmount: props.radiusPulseAmount }), createRootFragment(container, target));
    el.style.opacity = "0";
}
function autoInitGradients() {
    const gradientElements = document.querySelectorAll("[data-gradient]");
    gradientElements.forEach((el) => {
        addBlur(el);
    });
    const footerBlurElement = document.querySelector(".footer__blur");
    if (footerBlurElement) {
        addBlur(footerBlurElement, {
            height: 2300,
            colors: ["#C51C1C", "#66168D", "#E24814"],
            radiusVariation: 0.35,
            radiusPulseSpeed: 0.25,
        });
    }
}
export default GradientGlow;
export { autoInitGradients };
